import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { ExternalLink, Eye, Plus } from "lucide-react";
import {
  DEFAULT_DIAL_CODE,
  DIAL_CODES,
  RESERVED_SLUGS,
  chainToSettings,
  slugify,
  type Paginated,
} from "@gatiq/shared";
import { chosenRoleNames, HierarchyDragDropBuilder } from "@/components/HierarchyDragDropBuilder";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  Page,
  PageHeader,
  Pagination,
  SearchInput,
  StatusDot,
  TD,
  TDStacked,
  TH,
  THead,
  RowActions,
  TR,
  Table,
  TableSkeleton,
  TextInput,
  Toolbar,
  ToolbarCount,
  statusTone,
  useToast,
} from "@/ui";
import { api, ApiClientError } from "@/lib/api";
import { copyText } from "@/lib/clipboard";

/**
 * The API's `OrganizationListItemDto` (see `apps/api/src/services/organizations.ts`)
 * isn't part of `@gatiq/shared` — it is a super-admin-only shape — so it is
 * mirrored here rather than widening the shared contract for one screen.
 */
interface OrgListItem {
  id: string;
  name: string;
  slug: string;
  status: "active" | "suspended";
  logo: string | null;
  color1: string | null;
  adminEmail: string | null;
  adminPhone?: string | null;
  totalUsers: number;
  counts: {
    salesReps: number;
    counters: number;
    karigars: number;
    pendingApprovals: number;
  };
  createdAt: string;
}

interface CreateOrgResult {
  organization: { id: string; name: string; slug: string };
  admin: { id: string; name: string; email: string | null; phone: string | null; displayId: string | null };
  tempPassword: string;
  emailSent: boolean;
}

import { ListFilters, useListFilters } from "@/components/ListFilters";
const PAGE_SIZE = 20;

function useDebounced<T>(value: T, delay = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function OrganizationsPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [page, setPage] = useState(1);
  const filters = useListFilters(() => setPage(1));
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search);

  const [data, setData] = useState<Paginated<OrgListItem> | null>(null);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [createResult, setCreateResult] = useState<CreateOrgResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      filters.apply(qs);
      if (debouncedSearch) qs.set("q", debouncedSearch);
      const res = await api.get<Paginated<OrgListItem>>(`/platform/orgs?${qs.toString()}`);
      setData(res);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not load organizations.");
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, filters.apply, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  function openCreateModal() {
    setCreateResult(null);
    setModalOpen(true);
  }

  function handleCreated(result: CreateOrgResult) {
    setCreateResult(result);
    void load();
  }

  return (
    <Page bleed>
      <PageHeader title="Organizations" />

      <Card fill>
        <Toolbar
          trailing={
            <>
              {data ? <ToolbarCount count={data.total} noun="organization" /> : null}
              {<Button icon={<Plus aria-hidden="true" className="size-4" />} onClick={openCreateModal}>
                Create organization
              </Button>}
            </>
          }
        >
          <SearchInput
            label="Search organizations"
            onChange={(value) => setSearch(value)}
            placeholder="Search organizations…"
            value={search}
          />
          <ListFilters definitions={data?.filters} filters={filters} />
        </Toolbar>

        {loading ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <TableSkeleton cols={4} rows={6} />
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <EmptyState
              action={<Button icon={<Plus aria-hidden="true" className="size-4" />} onClick={openCreateModal}>
              Create organization
            </Button>}
              message={
                debouncedSearch
                  ? "No organization matches that search."
                  : "Create your first organization to get started."
              }
              title="No organizations yet"
            />
          </div>
        ) : (
          <>
            <Table fill searchable={false} filterable={false}>
              <THead>
                <TR>
                  <TH>Organization</TH>
                  <TH>Admin</TH>
                  <TH align="right">Users</TH>
                  <TH width="9rem">Status</TH>
                  <TH align="right" width="4.5rem">
                    <span className="sr-only">Actions</span>
                  </TH>
                </TR>
              </THead>
              <tbody>
                {data.items.map((org) => (
                  <TR key={org.id} onClick={() => navigate(`/super-admin/organizations/${org.id}`)}>
                    {/* Mark, name and slug are one identity, so they share one
                        cell. As three columns the mark left a near-empty
                        120px gutter and the slug competed with the name. */}
                    <TDStacked
                      avatar={
                        org.logo ? (
                          <img alt="" className="size-8 shrink-0 rounded-md object-cover" src={org.logo} />
                        ) : (
                          <span
                            aria-hidden="true"
                            className="grid size-8 shrink-0 place-items-center rounded-md text-xs font-semibold text-white"
                            style={{ backgroundColor: org.color1 ?? "var(--gq-color-1)" }}
                          >
                            {org.name.charAt(0).toUpperCase()}
                          </span>
                        )
                      }
                      primary={org.name}
                      secondary={<span className="font-mono">{org.slug}</span>}
                    />
                    <TD>{org.adminEmail ?? org.adminPhone ?? "—"}</TD>
                    <TD align="right">{org.totalUsers}</TD>
                    <TD>
                      <StatusDot
                        label={org.status === "active" ? "Active" : "Suspended"}
                        tone={statusTone(org.status)}
                      />
                    </TD>
                    <TD align="right">
                      <RowActions
                        items={[
                          {
                            label: "View details",
                            icon: Eye,
                            onSelect: () => void navigate(`/super-admin/organizations/${org.id}`),
                          },
                          {
                            label: "Open portal",
                            icon: ExternalLink,
                            onSelect: () => window.open(`/${org.slug}/login`, "_blank", "noopener"),
                          },
                        ]}
                        label={org.name}
                      />
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
            <div className="shrink-0">
              <Pagination onChange={setPage} page={data.page} total={data.total} totalPages={data.totalPages} />
            </div>
          </>
        )}
      </Card>

      {/* Service status lives on its own screen now — see
          pages/super-admin/SystemHealthPage.tsx. A one-line strip under a
          table was the wrong place for the platform's single point of
          failure (D-08). */}

      <CreateOrgModal
        onClose={() => setModalOpen(false)}
        onCreated={handleCreated}
        open={modalOpen}
        result={createResult}
      />
    </Page>
  );
}

type SlugStatus = "idle" | "checking" | "available" | "taken" | "reserved";

function CreateOrgModal({
  open,
  onClose,
  onCreated,
  result,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (result: CreateOrgResult) => void;
  result: CreateOrgResult | null;
}) {
  const toast = useToast();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPhone, setAdminPhone] = useState("");
  /* India by default — every org so far is Indian, and the rest of the
   * product is +91-only (see `intlPhoneSchema`). */
  const [adminDial, setAdminDial] = useState<string>(DEFAULT_DIAL_CODE);
  const [roleNames, setRoleNames] = useState<Record<string, string>>({});
  // The chain top to bottom, copies included (D-16).
  const [chain, setChain] = useState<string[]>([
    "sales_rep:1",
    "distributor:1",
    "counter",
    "karigar",
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [slugStatus, setSlugStatus] = useState<SlugStatus>("idle");

  const debouncedSlug = useDebounced(slug, 400);

  useEffect(() => {
    if (!slugTouched) setSlug(slugify(name));
  }, [name, slugTouched]);

  useEffect(() => {
    if (!debouncedSlug) {
      setSlugStatus("idle");
      return;
    }
    if (RESERVED_SLUGS.includes(debouncedSlug)) {
      setSlugStatus("reserved");
      return;
    }
    let cancelled = false;
    setSlugStatus("checking");
    // No dedicated availability endpoint exists — the org list search already
    // matches on slug, so an exact match there is exactly "taken".
    void api
      .get<Paginated<OrgListItem>>(`/platform/orgs?q=${encodeURIComponent(debouncedSlug)}&pageSize=5`)
      .then((res) => {
        if (cancelled) return;
        const taken = res.items.some((o) => o.slug === debouncedSlug);
        setSlugStatus(taken ? "taken" : "available");
      })
      .catch(() => {
        if (!cancelled) setSlugStatus("idle");
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSlug]);

  function reset() {
    setName("");
    setSlug("");
    setSlugTouched(false);
    setAdminName("");
    setAdminEmail("");
    setAdminPhone("");
    setChain(["sales_rep:1", "distributor:1", "counter", "karigar"]);
    setRoleNames({});
    setFieldErrors({});
    setSlugStatus("idle");
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSubmit() {
    setSubmitting(true);
    setFieldErrors({});
    try {
      const res = await api.post<CreateOrgResult>("/platform/orgs", {
        name,
        slug,
        admin: {
          name: adminName,
          email: adminEmail,
          phone: `${adminDial}${adminPhone.replace(/\D/g, "")}`,
        },
        ...chainToSettings(chain),
        roleNames: chosenRoleNames(roleNames),
      });
      onCreated(res);
      reset();
    } catch (err) {
      // Fields the form shows are highlighted; the message is always shown too, so a
      // problem in a field the form has no box for (a chain role name) is never silent.
      if (err instanceof ApiClientError && err.fields) setFieldErrors(err.fields);
      toast.error(err instanceof ApiClientError ? err.message : "Could not create the organization.");
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return (
      <Modal onClose={handleClose} open={open} title="Organization created" width="md">
        <div className="flex flex-col gap-4">
          <Alert kind="success" title={`${result.organization.name} is ready`}>
            Sign in at {window.location.host}/{result.organization.slug}/login
          </Alert>
          {result.emailSent ? (
            <Alert kind="info" title="Welcome email sent">
              A welcome email with these credentials was sent to {result.admin.email}.
            </Alert>
          ) : (
            <Alert kind={result.admin.email ? "warning" : "info"} title="Share these credentials">
              {result.admin.email
                ? `The welcome email to ${result.admin.email} did not go out.`
                : `${result.admin.name} signs in with ${result.admin.phone ?? "their mobile number"}.`}
            </Alert>
          )}
          <div className="rounded-md border border-rule bg-surface p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted">Temporary password</p>
            <div className="mt-2 flex items-center justify-between gap-3">
              <code className="font-mono text-lg tracking-wide text-ink">{result.tempPassword}</code>
              <Button
                onClick={() => {
                  void copyText(result.tempPassword).then((ok) =>
                    ok
                      ? toast.success("Copied to clipboard.")
                      : toast.error("Could not copy. Select the password and copy it by hand."),
                  );
                }}
                size="sm"
                variant="secondary"
              >
                Copy
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted">
              Shown once. Expires in 7 days or as soon as {result.admin.name.split(" ")[0]} signs in and
              changes it.
            </p>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button onClick={handleClose}>Done</Button>
        </div>
      </Modal>
    );
  }

  const canSubmit =
    name.trim().length > 1 &&
    slug.length > 1 &&
    adminName.trim().length > 1 &&
    adminPhone.trim().length > 5 &&
    slugStatus !== "taken" &&
    slugStatus !== "reserved" &&
    chain.length > 0;
  const host = window.location.host;

  return (
    <Modal
      description="Set up a new tenant: its name and address, its first administrator, and its distribution chain."
      footer={
        <>
          <Button disabled={submitting} onClick={handleClose} variant="secondary">
            Cancel
          </Button>
          <Button disabled={!canSubmit} loading={submitting} onClick={() => void handleSubmit()}>
            Create organization
          </Button>
        </>
      }
      onClose={handleClose}
      open={open}
      title="Create organization"
      width="xl"
    >
      <div className="flex flex-col gap-7">
        <FormSection step={1} title="Organization">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextInput
              error={fieldErrors.name}
              label="Company or brand name"
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Oliva Laminates"
              value={name}
            />
            <TextInput
              error={
                slugStatus === "taken"
                  ? "That address is already taken."
                  : slugStatus === "reserved"
                    ? "That name is reserved by the platform."
                    : fieldErrors.slug
              }
              hint={
                slugStatus === "checking"
                  ? "Checking availability…"
                  : slugStatus === "available"
                    ? `${host}/${slug}/login is available`
                    : `${host}/${slug || "…"}/login`
              }
              label="Portal address"
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value.toLowerCase());
              }}
              placeholder="e.g. oliva"
              value={slug}
            />
          </div>
        </FormSection>

        <FormSection step={2} title="First administrator">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <TextInput
              error={fieldErrors["admin.name"]}
              label="Full name"
              onChange={(e) => setAdminName(e.target.value)}
              placeholder="e.g. Rajesh Sharma"
              value={adminName}
            />
            <TextInput
              error={fieldErrors["admin.email"]}
              label="Work email"
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="admin@company.com"
              type="email"
              value={adminEmail}
            />
            <Field error={fieldErrors["admin.phone"]} label="Mobile number" required>
              {(id, invalid) => (
                <div className="flex items-stretch gap-2">
                  <select
                    aria-label="Country code"
                    className={`h-10 shrink-0 rounded-md border bg-raised px-2 text-sm text-ink ${
                      invalid ? "border-danger" : "border-rule"
                    }`}
                    onChange={(e) => setAdminDial(e.target.value)}
                    value={adminDial}
                  >
                    {DIAL_CODES.map((c) => (
                      <option key={c.iso} value={c.dial}>
                        {c.iso} {c.dial}
                      </option>
                    ))}
                  </select>
                  <input
                    aria-invalid={invalid}
                    className={`h-10 min-w-0 flex-1 rounded-md border bg-raised px-3 text-sm text-ink placeholder:text-muted/70 ${
                      invalid ? "border-danger focus:border-danger" : "border-rule"
                    }`}
                    id={id}
                    inputMode="numeric"
                    onChange={(e) => setAdminPhone(e.target.value)}
                    placeholder="98765 43210"
                    value={adminPhone}
                  />
                </div>
              )}
            </Field>
          </div>
        </FormSection>

        <FormSection step={3} title="Distribution chain">
          <HierarchyDragDropBuilder
            names={roleNames}
            onChange={setChain}
            onNamesChange={setRoleNames}
            orgName={name || "New organization"}
            value={chain}
          />
        </FormSection>
      </div>
    </Modal>
  );
}

/** A numbered step of the create form. */
function FormSection({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <span className="grid size-6 shrink-0 place-items-center rounded-full bg-brand-light font-title text-xs font-semibold text-brand-ink ring-1 ring-brand-border ring-inset">
          {step}
        </span>
        <h3 className="font-title text-[15px] font-semibold text-ink">{title}</h3>
      </div>
      <div className="sm:pl-9">{children}</div>
    </section>
  );
}
